// Ported from src/components/DeviceHistory/DeviceHistory.js on 2026-10-05.

import PageNumber from 'components/PageNumber/PageNumber';
import type { MRT_Cell } from 'material-react-table';
import type { BaseTableColumnDef } from 'components/Tables/baseTablePreferences';
import type { DeviceHistoryPoint } from 'features/Integrations/types/deviceControl.types';
import type { DeviceHistoryProps, DeviceHistoryRow } from './deviceHistory.types';
import BaseTable from 'components/Tables/BaseTable';
import LoadingDots from 'components/UI/LoadingDots';
import dayjs from 'dayjs';
import React, { useMemo, useState } from 'react';
import { describeSirenTone } from 'utils/devices/sirenTone.utils';

export default function DeviceHistory({
  data,
  title = 'Device History',
  subTitle,
  deviceType,
  hideHeader = false,
  tablePreferenceKey,
  tableOptionsClassName = '',
  loading = false,
  hasMore = false,
  onLoadMore,
  error = null,
}: DeviceHistoryProps) {
  // Dev-only render tracking
  try {
    if (process.env.NODE_ENV === 'development') {
      require('hooks/useRenderTracker').default('DeviceHistory');
    }
  } catch (e) {}
  const [pageNumber, setPageNumber] = useState(0);
  // Sirens get a Tone column: which tone or message each activation played.
  const isSirenHistory = deviceType === 'Technology Bridge_Siren/Giant Voice';
  // The hook hands back the API's own wording when it has one; this is the fallback.
  const historyErrorText = error?.message || 'The history could not be loaded.';

  const getStatusText = (point: DeviceHistoryPoint) => {
    if (!point) return '';

    const errorMsg = 'Error';
    const positiveMsg = deviceType === 'Technology Bridge_Siren/Giant Voice' ? 'Active' : 'Closed';
    const negativeMsg = deviceType === 'Technology Bridge_Siren/Giant Voice' ? 'Idle' : 'Open';

    const maintenanceMsg = 'Maintenance';
    const offlineMsg = 'Offline';
    const apiErrorMsg = 'API Error';

    //MRC status codes:
    // 0 = Open
    // 1 = Closed

    // Technology Bridge Only
    // Flashing Light/Siren status codes:
    // 0 = Open/Idle
    // 1 = Closed/Active
    // -1 = maintenance
    // -2 = offline
    // -3 = API error

    return point.status === -3
      ? apiErrorMsg
      : point.status === -1
        ? maintenanceMsg
        : point.status === -2
          ? offlineMsg
          : point.status === 0
            ? negativeMsg
            : positiveMsg;
  };

  const DataRow = ({ point }: { point: DeviceHistoryPoint }) => {
    if (!point) return null;
    const statusText = getStatusText(point);

    return (
      <div className="flex-fill flex-basis-0">
        <div className="d-none d-lg-block">
          <div className="row text-center">
            <div className="col-2">
              <p>{new Date(point.ts).toLocaleString()}</p>
            </div>
            <div className="col-2">
              <p>{point.node_id}</p>
            </div>
            <div className="col-2">
              <p>{statusText}</p>
            </div>
            <div className="col-3">
              <p className="text-truncate" title={point.source}>
                {point.source}
              </p>
            </div>
            <div className="col-3">
              <p className="text-truncate" title={point.comments}>
                {point.comments}
              </p>
            </div>
          </div>
        </div>

        <div className="d-block d-lg-none">
          <div className="row p-1">
            <div>
              <p>
                Date/Time: <span>{dayjs(point.ts).format('MM/DD/YY, hh:mm A')}</span>
              </p>
            </div>
            <div>
              <p>
                Device ID: <span>{point.node_id}</span>
              </p>
            </div>
            <div>
              <p>
                Status: <span>{statusText}</span>
              </p>
            </div>
            {isSirenHistory && (
              <div>
                <p>
                  Tone: <span>{describeSirenTone(point)}</span>
                </p>
              </div>
            )}
            <div>
              <p>
                Source: <span>{point.source}</span>
              </p>
            </div>
            <div>
              <p className="m-0">Comments:</p>
              <p className="ms-2 text-wrap">{point.comments}</p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Keep pagination stable by slicing to the current page.

  const pagedData = useMemo(() => {
    if (!Array.isArray(data)) return [];
    return data.slice(pageNumber * 6, pageNumber * 6 + 6);
  }, [data, pageNumber]);

  // Desktop layout uses BaseTable for consistent styling across the app.

  const columns = useMemo<BaseTableColumnDef<DeviceHistoryRow>[]>(
    () => [
      {
        accessorKey: 'ts',
        header: 'Date/Time',
        Cell: ({ cell }: { cell: MRT_Cell<DeviceHistoryRow> }) => (
          <span>{dayjs(cell.getValue<number>()).format('MM/DD/YY, hh:mm A')}</span>
        ),
      },
      {
        accessorKey: 'node_id',
        header: 'Device ID',
        Cell: ({ cell }) => <span>{cell.getValue<string>()}</span>,
      },
      {
        accessorKey: 'statusText',
        header: 'Status',
        Cell: ({ cell }) => <span>{cell.getValue<string>()}</span>,
      },
      ...(isSirenHistory
        ? [
            {
              accessorKey: 'toneText',
              header: 'Tone',
              Cell: ({ cell }: { cell: MRT_Cell<DeviceHistoryRow> }) => (
                <span
                  className="text-truncate d-inline-block w-100"
                  title={cell.getValue<string>()}
                >
                  {cell.getValue<string>()}
                </span>
              ),
            },
          ]
        : []),
      {
        accessorKey: 'source',
        header: 'Source',
        Cell: ({ cell }) => (
          <span className="text-truncate d-inline-block w-100" title={cell.getValue<string>()}>
            {cell.getValue<string>()}
          </span>
        ),
      },
      {
        accessorKey: 'comments',
        header: 'Comments',
        Cell: ({ cell }) => (
          <span className="text-truncate d-inline-block w-100" title={cell.getValue<string>()}>
            {cell.getValue<string>()}
          </span>
        ),
      },
    ],
    [isSirenHistory],
  );

  // Precompute status label for the table to avoid repeated lookups.

  const tableRows = useMemo<DeviceHistoryRow[]>(() => {
    if (!Array.isArray(pagedData)) return [];
    return pagedData.map((point) => ({
      ...point,
      statusText: getStatusText(point),
      toneText: isSirenHistory ? describeSirenTone(point) : undefined,
    }));
  }, [pagedData, deviceType, isSirenHistory]);

  return (
    <div className="d-flex flex-column align-items-center flex-fill w-100 rounded-2 bg-primary">
      {!hideHeader && (
        <>
          <h1 className="d-none d-lg-block fs-4">{title}</h1>
          <h4 className="d-block d-lg-none fs-6">{title}</h4>
          {subTitle && <h3 className="d-none d-lg-block fs-6">{subTitle}</h3>}
          {subTitle && (
            <p className="d-block d-lg-none border-bottom pb-2 w-100 text-center">{subTitle}</p>
          )}
        </>
      )}

      {/* List */}
      {data && (
        <div className="d-flex flex-column flex-grow-1 w-100 justify-content-start align-items-center">
          <div className="w-100 d-none d-lg-block">
            <BaseTable
              columns={columns}
              rows={tableRows}
              enableRowVirtualization={false}
              tablePreferenceKey={tablePreferenceKey}
              tableOptionsClassName={tableOptionsClassName}
              customStyles={{
                container: { backgroundColor: 'transparent' },
                header: { backgroundColor: '#2C373B' },
                cell: {
                  color: '#ffffff',
                  borderBottom: '1px solid #56577A',
                  padding: '8px',
                  whiteSpace: 'nowrap',
                },
              }}
            />
          </div>

          {data?.length > 0 && (
            <div className="d-flex d-lg-none">
              <DataRow point={data[pageNumber]} />
            </div>
          )}

          {data?.length === 0 &&
            (error ? (
              // A failed fetch is not an empty history: saying "NO DATA" here would
              // report a device as never activated when the request simply failed.
              <p className="text-danger m-0 py-2 text-center">{historyErrorText}</p>
            ) : loading ? (
              <LoadingDots className="py-2" label="Loading history" />
            ) : (
              <p>NO DATA</p>
            ))}

          <div className="d-none d-lg-block">
            <PageNumber
              pages={Math.ceil(data.length / 6)}
              value={pageNumber}
              onChange={setPageNumber}
            />
          </div>

          <div className="d-block d-lg-none">
            <PageNumber pages={data.length} value={pageNumber} onChange={setPageNumber} />
          </div>

          {/* One paging footer for every history surface. The page load holds ONE row
              per device, so the older rows are asked for here; the owning screen runs
              the fetch (useDeviceSeriesData) and this component stays presentational.
              `hasMore` is false after a failed page, so the button retries instead. */}
          {(hasMore || loading || error) && (
            <div className="d-flex flex-column align-items-center gap-1 py-1">
              {error && data?.length > 0 && (
                <span className="text-danger small text-center">{historyErrorText}</span>
              )}
              {onLoadMore && (
                <button
                  type="button"
                  className="btn btn-sm btn-outline-light"
                  disabled={loading}
                  onClick={onLoadMore}
                >
                  {loading ? <LoadingDots /> : error ? 'Retry' : 'Load more'}
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
